import React, { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Home, Inbox, Navigation, Wallet, Trophy, Bike, Power, LifeBuoy, User, MapPin, CheckCircle2, Clock, TrendingUp, Package } from "lucide-react";
import { BarChart, Bar, XAxis, ResponsiveContainer, Tooltip } from "recharts";
import { DashShell } from "@/components/DashShell";
import { KpiCard } from "@/components/common";
import SupportSelfService from "@/components/support/SupportSelfService";
import { useAuth } from "@/context/AuthContext";
import { api, inr, apiError } from "@/lib/api";

const NAV = [
  { key: "home", label: "Home", icon: Home },
  { key: "requests", label: "Requests", icon: Inbox },
  { key: "active", label: "Active Delivery", icon: Navigation },
  { key: "earnings", label: "Earnings", icon: Wallet },
  { key: "profile", label: "Profile", icon: User },
  { key: "support", label: "Support", icon: LifeBuoy },
];
const FLOW = [
  { stage: "accepted", label: "Accepted", action: "arrived_restaurant", btn: "Arrived at restaurant" },
  { stage: "arrived_restaurant", label: "At restaurant", action: "picked_up", btn: "Confirm pickup" },
  { stage: "picked_up", label: "Picked up", action: "on_the_way", btn: "Start delivery" },
  { stage: "on_the_way", label: "On the way", action: "arrived_customer", btn: "Arrived at customer" },
  { stage: "arrived_customer", label: "At customer", action: "otp", btn: "Verify delivery OTP" },
];

export default function DeliveryDashboard() {
  const { user, patchUser } = useAuth();
  const [tab, setTab] = useState("home");
  const [dash, setDash] = useState(null);
  const [reqs, setReqs] = useState([]);
  const [earn, setEarn] = useState(null);
  const [otpFor, setOtpFor] = useState(null);
  const [otp, setOtp] = useState("");
  const [pf, setPf] = useState({});
  const [sharing, setSharing] = useState(false);
  const watchRef = useRef(null);
  const lastSentRef = useRef(0);
  const activeIdsRef = useRef([]);

  useEffect(() => { if (user) setPf({ name: user.name || "", email: user.email || "", phone: user.phone || "", vehicle: user.vehicle || "", vehicle_number: user.vehicle_number || "", vehicle_model: user.vehicle_model || "", emergency_contact: user.emergency_contact || "", working_area: user.working_area || "", address: user.address || "" }); }, [user?.id]);
  const savePf = async () => { if (!pf.name?.trim()) return toast.error("Name required"); try { const { data } = await api.put("/profile", pf); patchUser(data); toast.success("Profile updated"); } catch (e) { toast.error(apiError(e)); } };

  const load = () => {
    api.get("/delivery/dashboard").then((r) => setDash(r.data)).catch(() => {});
    api.get("/delivery/requests").then((r) => setReqs(r.data)).catch(() => {});
    api.get("/delivery/earnings").then((r) => setEarn(r.data)).catch(() => {});
  };
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, []);

  useEffect(() => { activeIdsRef.current = (dash?.active_orders || []).map((o) => o.id); }, [dash]);
  const postLoc = (lat, lng) => Promise.all(activeIdsRef.current.map((id) =>
    api.post(`/delivery/orders/${id}/location`, { lat, lng }).catch(() => {})));
  const stopSharing = () => {
    if (watchRef.current != null) { navigator.geolocation.clearWatch(watchRef.current); watchRef.current = null; }
    setSharing(false);
  };
  const startSharing = () => {
    if (!("geolocation" in navigator)) return toast.error("Live location isn't supported on this device.");
    const wid = navigator.geolocation.watchPosition(
      (pos) => {
        const now = Date.now();
        if (now - lastSentRef.current < 8000) return;
        lastSentRef.current = now;
        postLoc(pos.coords.latitude, pos.coords.longitude);
      },
      (err) => {
        if (err.code === 1) toast.error("Location permission denied — customers can't track you live.");
        else toast.error("Couldn't get your location. Please check GPS and try again.");
        stopSharing();
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 20000 }
    );
    watchRef.current = wid;
    setSharing(true);
    toast.success("Live location sharing is on");
  };
  useEffect(() => () => { if (watchRef.current != null) navigator.geolocation.clearWatch(watchRef.current); }, []);
  useEffect(() => { if (sharing && (dash?.active_orders || []).length === 0) stopSharing(); }, [dash?.active_orders?.length]);

  const toggleOnline = async () => { const { data } = await api.post("/delivery/online", { online: !dash.online }); setDash((d) => ({ ...d, online: data.online })); toast.success(data.online ? "You're online" : "You're offline"); };
  const accept = async (o) => { try { await api.post(`/delivery/requests/${o.id}/accept`); toast.success("Request accepted"); setTab("active"); load(); } catch (e) { toast.error(apiError(e)); } };
  const reject = async (o) => { try { await api.post(`/delivery/requests/${o.id}/reject`); toast.success("Passed to another rider"); load(); } catch (e) { toast.error(apiError(e)); } };
  const advance = async (o, action) => {
    if (action === "otp") { setOtpFor(o); setOtp(""); return; }
    try { await api.post(`/delivery/orders/${o.id}/advance`, { stage: action }); toast.success("Updated"); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const verifyOtp = async () => {
    try { const { data } = await api.post(`/delivery/orders/${otpFor.id}/verify-otp`, { otp }); toast.success(`Delivered! You earned ${inr(data.earning || 0)} 🎉`); setOtpFor(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <DashShell title="Rider App" subtitle="Delivery Partner" nav={NAV} active={tab} onNav={setTab}>
      {tab === "home" && dash && (
        <div className="space-y-4 max-w-3xl">
          <div className={`rounded-2xl p-4 flex items-center justify-between text-white ${dash.online ? "bg-veg" : "bg-ink"}`}>
            <div><div className="font-bold text-lg flex items-center gap-2"><Bike size={22} /> {dash.online ? "You're Online" : "You're Offline"}</div><div className="text-sm text-white/85">{dash.online ? `${dash.requests_count} request(s) waiting` : "Go online to start earning"}</div></div>
            <button onClick={toggleOnline} className="bg-white/20 rounded-full w-12 h-12 flex items-center justify-center tap" data-testid="online-toggle"><Power size={22} /></button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard icon={Wallet} label="Today's Earnings" value={inr(dash.today_earnings)} tone="coral" />
            <KpiCard icon={Package} label="Deliveries Today" value={dash.completed_today} tone="veg" />
            <KpiCard icon={Navigation} label="Active" value={dash.active_orders.length} tone="gold" />
            <KpiCard icon={TrendingUp} label="Rating" value={dash.rating} tone="ink" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard icon={Wallet} label="This Week" value={inr(dash.week_earnings)} />
            <KpiCard icon={Wallet} label="This Month" value={inr(dash.month_earnings)} />
            <KpiCard icon={CheckCircle2} label="Acceptance" value={`${dash.acceptance_rate}%`} tone="veg" />
            <KpiCard icon={CheckCircle2} label="Completion" value={`${dash.completion_rate}%`} tone="veg" />
          </div>
          {dash.requests_count > 0 && <button onClick={() => setTab("requests")} className="w-full bg-coral text-white font-bold py-3 rounded-xl tap" data-testid="view-requests">View {dash.requests_count} delivery request(s)</button>}
        </div>
      )}

      {tab === "requests" && (
        <div className="max-w-2xl space-y-3">
          <h3 className="font-bold text-ink">Delivery Requests ({reqs.length})</h3>
          {reqs.length === 0 && <div className="bg-white rounded-2xl border border-border p-8 text-center text-ink-600" data-testid="no-requests">No requests right now. Stay online to receive orders.</div>}
          {reqs.map((r) => (
            <div key={r.id} className="bg-white rounded-2xl border border-border p-4" data-testid={`request-${r.code}`}>
              <div className="flex items-center justify-between"><span className="font-bold text-ink">{r.restaurant_name}</span><span className="text-veg font-extrabold">{inr(r.earnings)}</span></div>
              <div className="text-xs text-ink-600 mt-0.5">#{(r.code || "").slice(-4)} · Order value {inr(r.order_value)}</div>
              <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                <div className="bg-surface rounded-xl p-2"><div className="text-[10px] text-ink-600">Pickup</div><div className="font-bold text-sm">{r.pickup_distance} km</div></div>
                <div className="bg-surface rounded-xl p-2"><div className="text-[10px] text-ink-600">Drop</div><div className="font-bold text-sm">{r.delivery_distance} km</div></div>
                <div className="bg-surface rounded-xl p-2"><div className="text-[10px] text-ink-600">ETA</div><div className="font-bold text-sm">{r.eta_min} min</div></div>
              </div>
              <div className="text-xs text-ink-600 mt-2 flex items-center gap-1"><MapPin size={12} /> Drop: {r.drop_area}</div>
              {r.instructions && <div className="text-xs text-amber-700 mt-1">✎ {r.instructions}</div>}
              <div className="flex gap-2 mt-3">
                <button onClick={() => accept(r)} className="flex-1 bg-veg text-white font-bold py-2.5 rounded-xl text-sm tap" data-testid={`accept-req-${r.code}`}>Accept</button>
                <button onClick={() => reject(r)} className="flex-1 bg-red-50 text-bad font-bold py-2.5 rounded-xl text-sm tap" data-testid={`reject-req-${r.code}`}>Reject</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "active" && dash && (
        <div className="max-w-2xl space-y-3">
          <h3 className="font-bold text-ink">Active Deliveries ({dash.active_orders.length})</h3>
          {dash.active_orders.length > 0 && (
            <div className="bg-white rounded-2xl border border-border p-4 flex items-center gap-3" data-testid="location-sharing-card">
              <div className="w-10 h-10 rounded-xl bg-coral-light flex items-center justify-center shrink-0"><MapPin size={18} className={sharing ? "text-veg" : "text-coral"} /></div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm">Live location sharing</div>
                <div className="text-xs text-ink-600">{sharing ? "On — customers can track you in real time" : "Turn on so customers can see you on the map"}</div>
              </div>
              <button onClick={sharing ? stopSharing : startSharing} data-testid="toggle-location-sharing" className={`px-4 py-2 rounded-xl font-bold text-sm tap ${sharing ? "bg-veg text-white" : "bg-coral text-white"}`}>{sharing ? "On" : "Enable"}</button>
            </div>
          )}
          {dash.active_orders.length === 0 && <div className="bg-white rounded-2xl border border-border p-8 text-center text-ink-600">No active deliveries. Accept a request to start.</div>}
          {dash.active_orders.map((o) => {
            const step = FLOW.find((f) => f.stage === (o.delivery_stage || "accepted"));
            const idx = FLOW.findIndex((f) => f.stage === (o.delivery_stage || "accepted"));
            return (
              <div key={o.id} className="bg-white rounded-2xl border border-border p-4" data-testid={`active-${o.code}`}>
                <div className="flex items-center justify-between"><span className="font-bold text-ink">{o.restaurant_name}</span><span className="text-sm text-ink-600">#{(o.code || "").slice(-4)}</span></div>
                <div className="text-xs text-ink-600 mt-0.5">{o.items?.map((i) => `${i.qty}× ${i.name}`).join(", ")}</div>
                <div className="flex items-center gap-1 my-3">
                  {FLOW.map((f, i) => <div key={f.stage} className={`flex-1 h-1.5 rounded-full ${i <= idx ? "bg-veg" : "bg-border"}`} />)}
                </div>
                <div className="text-xs font-semibold text-ink mb-2">Current: {step?.label}</div>
                {o.address && <div className="text-xs text-ink-600 flex items-center gap-1 mb-2"><MapPin size={12} /> {o.address.line || o.address.label} · {o.address.area || ""}</div>}
                {step && <button onClick={() => advance(o, step.action)} className="w-full bg-coral text-white font-bold py-2.5 rounded-xl text-sm tap" data-testid={`advance-${o.code}`}>{step.btn}</button>}
              </div>
            );
          })}
        </div>
      )}

      {tab === "earnings" && earn && (
        <div className="max-w-3xl space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard icon={Wallet} label="Total Earnings" value={inr(earn.total)} tone="coral" />
            <KpiCard icon={TrendingUp} label="Avg / Delivery" value={inr(earn.avg_per_delivery)} tone="veg" />
            <KpiCard icon={Clock} label="Pending Payout" value={inr(earn.pending_payout)} tone="gold" />
            <KpiCard icon={CheckCircle2} label="Paid Payout" value={inr(earn.paid_payout)} tone="ink" />
          </div>
          <div className="bg-white rounded-2xl border border-border p-4">
            <h3 className="font-bold text-ink mb-2">Daily earnings</h3>
            {earn.chart.length === 0 ? <p className="text-sm text-ink-600 py-6 text-center">No earnings yet. Complete deliveries to see your trend.</p> : (
              <ResponsiveContainer width="100%" height={180}><BarChart data={earn.chart}><XAxis dataKey="date" fontSize={11} /><Tooltip /><Bar dataKey="earnings" fill="#FF6B35" radius={[6, 6, 0, 0]} /></BarChart></ResponsiveContainer>
            )}
          </div>
          <div className="bg-white rounded-2xl border border-border p-4">
            <h3 className="font-bold text-ink mb-2">Delivery-wise earnings</h3>
            <div className="overflow-x-auto"><table className="w-full text-sm min-w-[560px]"><thead><tr className="text-left text-xs text-ink-600 uppercase border-b border-border">{["Order", "Date", "Restaurant", "Distance", "Base", "Tip", "Total"].map((c) => <th key={c} className="py-2 px-2">{c}</th>)}</tr></thead>
              <tbody>{earn.rows.map((e) => <tr key={e.id} className="border-b border-border last:border-0"><td className="py-2 px-2 font-semibold">#{(e.order_code || "").slice(-4)}</td><td className="px-2">{e.date}</td><td className="px-2">{e.restaurant_name}</td><td className="px-2">{e.distance} km</td><td className="px-2">{inr(e.base)}</td><td className="px-2">{inr(e.tip)}</td><td className="px-2 font-bold">{inr(e.total)}</td></tr>)}
              {earn.rows.length === 0 && <tr><td colSpan={7} className="text-center text-ink-600 py-6">No deliveries yet.</td></tr>}</tbody></table></div>
          </div>
        </div>
      )}

      {tab === "profile" && (
        <div className="max-w-xl space-y-4">
          <div className="bg-white rounded-2xl border border-border p-4">
            <div className="flex items-center gap-3 mb-3"><div className="w-14 h-14 rounded-full bg-coral-light flex items-center justify-center text-coral font-bold text-xl">{pf.name?.[0] || "D"}</div><div><div className="font-bold text-ink">{pf.name}</div><div className="text-xs text-ink-600">Delivery Partner · {user?.status || "active"}</div></div></div>
            <div className="grid sm:grid-cols-2 gap-2">
              {[["Full name", "name"], ["Phone", "phone"], ["Email", "email"], ["Emergency contact", "emergency_contact"], ["Vehicle type", "vehicle"], ["Vehicle number", "vehicle_number"], ["Vehicle model", "vehicle_model"], ["Preferred area", "working_area"], ["Address", "address"]].map(([l, k]) => (
                <div key={k}><label className="text-[11px] font-semibold text-ink-600 mb-0.5 block">{l}</label><input value={pf[k] || ""} onChange={(e) => setPf((p) => ({ ...p, [k]: e.target.value }))} className="w-full bg-surface rounded-lg px-3 py-2 text-sm outline-none border border-border" data-testid={`dp-${k}`} /></div>
              ))}
            </div>
            <button onClick={savePf} className="mt-3 bg-coral text-white font-bold px-5 py-2.5 rounded-xl text-sm tap" data-testid="save-dp-profile">Save changes</button>
          </div>
        </div>
      )}

      {tab === "support" && <div className="max-w-2xl"><SupportSelfService /></div>}

      {otpFor && (
        <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={() => setOtpFor(null)}>
          <div className="bg-white rounded-2xl p-5 max-w-xs w-full text-center" onClick={(e) => e.stopPropagation()} data-testid="otp-modal">
            <h3 className="font-bold text-ink mb-1">Delivery OTP</h3>
            <p className="text-xs text-ink-600 mb-3">Ask the customer for their 4-digit delivery OTP.</p>
            <input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="0000" className="w-full text-center text-2xl font-bold tracking-[0.5em] bg-surface rounded-xl py-3 outline-none border border-border" data-testid="otp-input" />
            <div className="flex gap-2 mt-4"><button onClick={() => setOtpFor(null)} className="flex-1 border border-border rounded-xl py-2.5 font-semibold text-sm">Cancel</button><button onClick={verifyOtp} className="flex-1 bg-veg text-white rounded-xl py-2.5 font-bold text-sm" data-testid="verify-otp">Confirm delivery</button></div>
          </div>
        </div>
      )}
    </DashShell>
  );
}
